// Theme utility functions for consistent theme-aware styling

export interface ThemeColors {
  // Background colors
  primary: string;
  secondary: string;
  surface: string;
  card: string;

  // Text colors
  text: {
    primary: string;
    secondary: string;
    muted: string;
    disabled: string;
  };

  // Border colors
  border: {
    light: string;
    medium: string;
    strong: string;
  };

  // Interactive colors
  hover: {
    primary: string;
    secondary: string;
  };

  // Form colors
  input: {
    background: string;
    border: string;
    text: string;
    placeholder: string;
  };
}

export const lightTheme: ThemeColors = {
  primary: 'bg-gray-50',
  secondary: 'bg-gray-100',
  surface: 'bg-white',
  card: 'bg-white',

  text: {
    primary: 'text-gray-900',
    secondary: 'text-gray-700',
    muted: 'text-gray-600',
    disabled: 'text-gray-400'
  },

  border: {
    light: 'border-gray-200',
    medium: 'border-gray-300',
    strong: 'border-gray-400'
  },

  hover: {
    primary: 'hover:bg-gray-100',
    secondary: 'hover:bg-gray-200'
  },

  input: {
    background: 'bg-white',
    border: 'border-gray-300',
    text: 'text-gray-900',
    placeholder: 'placeholder-gray-400'
  }
};

export const darkTheme: ThemeColors = {
  primary: 'bg-gray-900',
  secondary: 'bg-gray-800',
  surface: 'bg-gray-800',
  card: 'bg-gray-800',

  text: {
    primary: 'text-white',
    secondary: 'text-gray-300',
    muted: 'text-gray-400',
    disabled: 'text-gray-500'
  },

  border: {
    light: 'border-gray-700',
    medium: 'border-gray-600',
    strong: 'border-gray-500'
  },

  hover: {
    primary: 'hover:bg-gray-800',
    secondary: 'hover:bg-gray-700'
  },

  input: {
    background: 'bg-gray-700',
    border: 'border-gray-600',
    text: 'text-white',
    placeholder: 'placeholder-gray-400'
  }
};

// Utility functions
export const getThemeColors = (isDarkMode: boolean): ThemeColors => {
  return isDarkMode ? darkTheme : lightTheme;
};

export const getThemeClass = (isDarkMode: boolean, lightClass: string, darkClass: string): string => {
  return isDarkMode ? darkClass : lightClass;
};

// Common theme class combinations
export const getPageBackground = (isDarkMode: boolean): string => {
  return isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
};

export const getCardBackground = (isDarkMode: boolean): string => {
  return isDarkMode ? 'bg-gray-800' : 'bg-white';
};

export const getPrimaryText = (isDarkMode: boolean): string => {
  return isDarkMode ? 'text-white' : 'text-gray-900';
};

export const getSecondaryText = (isDarkMode: boolean): string => {
  return isDarkMode ? 'text-gray-300' : 'text-gray-700';
};

export const getMutedText = (isDarkMode: boolean): string => {
  return isDarkMode ? 'text-gray-400' : 'text-gray-600';
};

export const getPrimaryBorder = (isDarkMode: boolean): string => {
  return isDarkMode ? 'border-gray-600' : 'border-gray-300';
};

export const getLightBorder = (isDarkMode: boolean): string => {
  return isDarkMode ? 'border-gray-700' : 'border-gray-200';
};

export const getInputStyles = (isDarkMode: boolean): string => {
  return isDarkMode
    ? 'bg-gray-700 border border-gray-600 text-white placeholder-gray-400'
    : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400';
};

export const getButtonStyles = (isDarkMode: boolean) => {
  return {
    primary: isDarkMode
      ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600'
      : 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500',
    secondary: isDarkMode
      ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
    danger: isDarkMode
      ? 'bg-red-600 text-white hover:bg-red-700 border-red-600'
      : 'bg-red-500 text-white hover:bg-red-600 border-red-500'
  };
};

export const getTableRowStyles = (isDarkMode: boolean) => {
  return {
    header: isDarkMode
      ? 'bg-gray-700 text-gray-200 border-gray-600'
      : 'bg-gray-100 text-gray-700 border-gray-200',
    row: isDarkMode
      ? 'bg-gray-800 hover:bg-gray-700 border-gray-700'
      : 'bg-white hover:bg-gray-50 border-gray-200',
    even: isDarkMode
      ? 'bg-gray-750 hover:bg-gray-700 border-gray-700'
      : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
  };
};

export const getModalStyles = (isDarkMode: boolean): string => {
  return isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300';
};

// Helper function to convert gray-600 to gray-800 in dark mode
// Use this for all instances where gray-600 is used in light mode
export const getGray600Color = (isDarkMode: boolean): string => {
  return isDarkMode ? '#4b5563' : '#6b7280'; // gray-800 : gray-600
};

export const getGray600Class = (isDarkMode: boolean): string => {
  return isDarkMode ? 'text-gray-800' : 'text-gray-600';
};

export const getGray600BorderClass = (isDarkMode: boolean): string => {
  return isDarkMode ? 'border-gray-800' : 'border-gray-600';
};

export const getGray600BgClass = (isDarkMode: boolean): string => {
  return isDarkMode ? 'bg-gray-800' : 'bg-gray-600';
};